import {
  EMPLOYEE_TOKEN_PATTERN,
  MENTION_DELIMITER_CLASS,
  escapeRegExp,
  replaceRecruitersIdWithName,
} from './recruiters.utils'

export interface MentionableEmployee {
  label: string
  value: string
}

export interface MentionSegment {
  text: string
  mention?: MentionableEmployee
}

// Same rule replaceNamesWithRecruitersId applies on save: longest label
// first, and a delimiter or the end of the text after the name.
const buildMentionPattern = (recruiters: MentionableEmployee[]) => {
  const labels = recruiters
    .map((recruiter) => recruiter.label.trim())
    .filter(Boolean)
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp)
  return labels.length
    ? new RegExp(
        `@(?:${labels.join('|')})(?=${MENTION_DELIMITER_CLASS}|$)`,
        'g'
      )
    : null
}

/**
 * Splits note text into plain runs and recognised @-mentions. Stored
 * `{{employee:id}}` tokens are shown by name first, so saved notes and the
 * composer's draft text segment the same way.
 */
export const segmentMentions = (
  content: string | null | undefined,
  recruiters: MentionableEmployee[] | null | undefined
): MentionSegment[] => {
  const employees = recruiters ?? []
  const text = replaceRecruitersIdWithName(content, employees)
  if (!text) return []
  const pattern = buildMentionPattern(employees)
  if (!pattern) return [{ text }]

  // First employee wins on duplicate names, as replaceNamesWithRecruitersId does.
  const byMention = new Map<string, MentionableEmployee>()
  for (const recruiter of employees) {
    const key = `@${recruiter.label.trim()}`
    if (!byMention.has(key)) byMention.set(key, recruiter)
  }
  const segments: MentionSegment[] = []
  let last = 0
  for (const match of text.matchAll(pattern)) {
    if (match.index > last) {
      segments.push({ text: text.slice(last, match.index) })
    }
    segments.push({ text: match[0], mention: byMention.get(match[0]) })
    last = match.index + match[0].length
  }
  if (last < text.length) segments.push({ text: text.slice(last) })
  return segments
}

export interface MentionTag {
  referenceId: string
  tagAction: 'MENTION'
  tagType: 'EMPLOYEE'
}

/**
 * One EMPLOYEE/MENTION tag per distinct `@{{employee:id}}` token. Every token
 * id was a valid employee when written, so none are dropped: a note edited
 * after an employee left the recruiter list keeps that employee's tag.
 */
export const buildMentionTags = (
  taggedContent: string | null | undefined
): MentionTag[] => {
  const ids = new Set<string>()
  for (const match of (taggedContent ?? '').matchAll(EMPLOYEE_TOKEN_PATTERN)) {
    ids.add(match[1])
  }
  return [...ids].map((referenceId) => ({
    referenceId,
    tagAction: 'MENTION' as const,
    tagType: 'EMPLOYEE' as const,
  }))
}
